<script setup>
import { computed, ref, watch } from 'vue';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { messageStamp } from 'shared/helpers/timeHelper';
import { useMapGetter } from 'dashboard/composables/store';
import Avatar from 'next/avatar/Avatar.vue';
import MessagePreview from './MessagePreview.vue';
import InboxName from '../InboxName.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import UnreadBadge from 'dashboard/components-next/Conversation/ConversationCard/UnreadBadge.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import {
  LEGACY_CONVERSATION_STAGE_LABEL_MAP,
  isConversationStageLabel,
  normalizeConversationStageLabel,
} from 'dashboard/constants/conversationStages';

const props = defineProps({
  chat: { type: Object, required: true },
  currentContact: { type: Object, required: true },
  assignee: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
  isActiveChat: { type: Boolean, default: false },
  showAssignee: { type: Boolean, default: false },
  showInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
});

const emit = defineEmits([
  'click',
  'contextmenu',
  'selectConversation',
  'deSelectConversation',
]);

const hovered = ref(false);
const accountLabels = useMapGetter('labels/getLabels');

const fallbackAccentColors = [
  '#2563eb',
  '#7c3aed',
  '#0891b2',
  '#ea580c',
  '#16a34a',
  '#dc2626',
  '#4f46e5',
];

const unreadCount = computed(() => props.chat.unread_count);
const hasUnread = computed(() => unreadCount.value > 0);
const lastMessageInChat = computed(() => getLastMessage(props.chat));
const exactLastMessageTime = computed(() => {
  const timestamp = lastMessageInChat.value?.created_at || props.chat.timestamp;
  if (!timestamp) return '';

  const messageDate = new Date(timestamp * 1000);
  const today = new Date();
  const isToday =
    messageDate.getFullYear() === today.getFullYear() &&
    messageDate.getMonth() === today.getMonth() &&
    messageDate.getDate() === today.getDate();

  return messageStamp(timestamp, isToday ? 'HH:mm' : 'dd/MM HH:mm');
});
const exactLastMessageTimeColor = computed(() => {
  const lastMessage = lastMessageInChat.value || props.chat.last_non_activity_message;
  const messageType = lastMessage?.message_type;
  const senderType = lastMessage?.sender_type;
  const senderObjectType = lastMessage?.sender?.type;

  if ([0, '0', 'incoming'].includes(messageType)) {
    return '#dc2626';
  }

  if ([1, '1', 'outgoing'].includes(messageType)) {
    return '#2563eb';
  }

  if (senderType === 'Contact' || senderObjectType === 'contact') {
    return '#dc2626';
  }

  if (senderType === 'User' || senderObjectType === 'user') {
    return '#2563eb';
  }

  return '';
});

const voiceCallData = computed(() => {
  const last = lastMessageInChat.value;
  if (last?.content_type !== 'voice_call' || !last.call) {
    return { status: null, direction: null };
  }
  return {
    status: last.call.status,
    direction: last.call.direction === 'outgoing' ? 'outbound' : 'inbound',
  };
});

const showMetaSection = computed(() => {
  return (
    props.showInboxName ||
    (props.showAssignee && props.assignee.name) ||
    props.chat.priority
  );
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const showLabelsSection = computed(() => {
  return props.chat.labels?.length > 0 || hasSlaPolicyId.value;
});

const stageAccentColor = computed(() => {
  const labels = props.chat.labels || [];
  const stageLabels = accountLabels.value.filter(isConversationStageLabel);

  const savedStage = stageLabels.find(stage =>
    labels.some(
      label =>
        normalizeConversationStageLabel(label) ===
        normalizeConversationStageLabel(stage.title)
    )
  );

  if (savedStage?.color) return savedStage.color;

  const legacyStage = Object.entries(LEGACY_CONVERSATION_STAGE_LABEL_MAP).find(
    ([legacyLabel]) => labels.includes(legacyLabel)
  );

  if (legacyStage) {
    const mappedStage = stageLabels.find(
      stage =>
        normalizeConversationStageLabel(stage.title) ===
        normalizeConversationStageLabel(legacyStage[1])
    );
    if (mappedStage?.color) return mappedStage.color;
  }

  const firstRegularLabel = accountLabels.value.find(
    label => labels.includes(label.title) && !isConversationStageLabel(label)
  );
  if (firstRegularLabel?.color) return firstRegularLabel.color;

  return fallbackAccentColors[
    Number(props.chat.id || 0) % fallbackAccentColors.length
  ];
});

const cardAccentStyle = computed(() => ({
  borderLeftColor: stageAccentColor.value,
  boxShadow: `inset 3px 0 0 ${stageAccentColor.value}`,
}));

const avatarAccentStyle = computed(() => ({
  boxShadow: `0 0 0 2px ${stageAccentColor.value}22`,
}));

const messagePreviewClass = computed(() => {
  return [
    hasUnread.value ? 'font-medium text-n-slate-12' : 'text-n-slate-11',
    !props.compact && hasUnread.value ? 'ltr:pr-4 rtl:pl-4' : '',
    props.compact && hasUnread.value ? 'ltr:pr-6 rtl:pl-6' : '',
  ];
});

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, props.inbox.id);
  } else {
    emit('deSelectConversation', props.chat.id, props.inbox.id);
  }
};

const selectedModel = computed({
  get: () => props.selected,
  set: value => onSelectConversation(value),
});

watch(
  () => props.chat.id,
  () => {
    hovered.value = false;
  }
);
</script>

<template>
  <div
    class="relative flex items-start flex-grow-0 flex-shrink-0 w-auto max-w-[calc(100%-0.75rem)] py-0 my-1 mx-1.5 cursor-pointer conversation border border-solid border-n-weak rounded-lg bg-n-background hover:border-n-blue-7 hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 group hover:z-[1] transition-colors before:content-[none] before:absolute before:-top-px before:inset-x-0 before:h-px before:bg-n-surface-1 before:pointer-events-none hover:before:content-['']"
    :class="{
      'active animate-card-select !bg-n-blue-2 dark:!bg-n-blue-3 !border-n-blue-8 ring-1 ring-n-blue-7 shadow-sm':
        isActiveChat,
      'selected bg-n-slate-2 !border-n-surface-1': selected,
      'px-0': compact,
      'px-2': !compact,
    }"
    :style="cardAccentStyle"
    @click="$emit('click', $event)"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <span
      class="absolute size-2 rounded-full ltr:left-2 rtl:right-2 top-2"
      :style="{ backgroundColor: stageAccentColor }"
    />
    <div
      class="relative"
      :style="avatarAccentStyle"
      :class="[
        !hideThumbnail ? 'mt-3 rounded-full bg-n-alpha-1' : '',
        !showInboxName ? '' : 'mt-3',
      ]"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        v-if="!hideThumbnail"
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="28"
        :status="currentContact.availability_status"
        :class="showInboxName ? 'mt-3' : ''"
        hide-offline-status
      >
        <template #overlay="{ size }">
          <label
            v-if="hovered || selected"
            class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
            :style="{ width: `${size}px`, height: `${size}px` }"
            @click.stop
          >
            <Checkbox v-model="selectedModel" />
          </label>
        </template>
      </Avatar>
    </div>
    <div class="px-0 py-2 flex-1 min-w-0 border-line">
      <div
        v-if="showMetaSection"
        class="flex items-center min-w-0 gap-1"
        :class="{
          'ltr:ml-2 rtl:mr-2': !compact,
          'mx-2': compact,
        }"
      >
        <InboxName v-if="showInboxName" :inbox="inbox" class="flex-1 min-w-0" />
        <div
          class="flex items-baseline gap-2 flex-shrink-0"
          :class="{
            'flex-1 justify-between': !showInboxName,
          }"
        >
          <span
            v-if="showAssignee && assignee.name"
            class="text-n-slate-11 text-[11px] font-medium leading-3 py-0.5 px-0 inline-flex items-center truncate"
          >
            <fluent-icon icon="person" size="12" class="text-n-slate-11" />
            {{ assignee.name }}
          </span>
          <CardPriorityIcon
            :priority="chat.priority"
            class="flex-shrink-0 !size-3.5"
          />
        </div>
      </div>
      <h4
        class="conversation--user text-[13px] leading-4 my-0 mx-2 capitalize pt-0.5 text-ellipsis overflow-hidden whitespace-nowrap flex-1 min-w-0 ltr:pr-16 rtl:pl-16 text-n-slate-12"
        :class="hasUnread ? 'font-semibold' : 'font-medium'"
      >
        {{ currentContact.name }}
      </h4>
      <VoiceCallStatus
        v-if="voiceCallData.status"
        key="voice-status-row"
        :status="voiceCallData.status"
        :direction="voiceCallData.direction"
        :message-preview-class="messagePreviewClass"
      />
      <MessagePreview
        v-else-if="lastMessageInChat"
        key="message-preview"
        :message="lastMessageInChat"
        class="my-0 mx-2 leading-5 h-5 flex-1 min-w-0 text-xs"
        :class="messagePreviewClass"
      />
      <p
        v-else
        key="no-messages"
        class="text-n-slate-11 text-xs my-0 mx-2 leading-5 h-5 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
        :class="messagePreviewClass"
      >
        <fluent-icon
          size="16"
          class="-mt-0.5 align-middle inline-block text-n-slate-10"
          icon="info"
        />
        <span class="mx-0.5">
          {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
        </span>
      </p>
      <div
        class="absolute flex flex-col ltr:right-3 rtl:left-3"
        :class="showMetaSection ? 'top-7' : 'top-3'"
      >
        <span class="ml-auto font-normal leading-4 text-[11px]">
          <span :style="{ color: exactLastMessageTimeColor }">
            {{ exactLastMessageTime }}
          </span>
        </span>
        <div
          v-if="hasUnread"
          class="mt-0.5 flex items-center justify-end gap-1"
        >
          <span class="size-1.5 rounded-full bg-n-teal-9" />
          <UnreadBadge :count="unreadCount" class="ltr:ml-auto rtl:mr-auto" />
        </div>
      </div>
      <CardLabels
        v-if="showLabelsSection"
        :conversation-labels="chat.labels"
        class="mt-0.5 mx-2 mb-0"
      >
        <template v-if="hasSlaPolicyId" #before>
          <SLACardLabel :chat="chat" class="ltr:mr-1 rtl:ml-1" />
        </template>
      </CardLabels>
    </div>
  </div>
</template>
